import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Desktop, Mobile } from '../../../App';
import Banner from '../../../components/common/Banner';
import { SidebarModal } from '../../../components/common/Sidebar/Sidebar';
import SidebarNav from '../../../components/common/SidebarNav';
import {
  dateDifferenceInDaysFromToday,
  SidebarIcon,
  SidebarNavLinks,
  useOnClickOutside,
  isPartnerHelper
} from '../../../helpers/helperFunctions';
import { useWindowWide } from '../../../helpers/wideScreen';
import { P2 } from '../../../styles/FontStyles';
import {
  ArrowPolygon,
  Flexed,
  UserImageCircle
} from '../../../styles/GlobalComponents';
import { color, colors, globalImages } from '../../../styles/GlobalStyles';
import {
  HamburgerMenuIcon,
  Icon,
  IconHome,
  IconMenu,
  IconNotifications,
  IconSettings,
  IconTelegramNoBorder
} from '../../../styles/Icons';
import { FlexCenter } from '../../../styles/stylesStyled';
import Notifications from '../../../views/Notifications/Notifications';

import {
  DASHBOARD,
  MY_HUBS,
  SETTINGS_ACCOUNT,
  SETTINGS_LAYOUT
} from '../../../routes/routes';
import {
  MainNav,
  Menu,
  TopNavigation,
  TopNavigationMain
} from '../BackofficeLayoutStyled';

import { AnimatePresence } from 'framer-motion';
import { useAppSelector } from '../../../app/hooks';
import { getNotifications } from '../../../slices/notifications-slice';
import { ProfileDropdown } from './ProfileDropdown';
import { ShowHide } from './ShowHide';
import { UserTypes } from '../../../types/loginTypes';
import { getCurrentLicense } from '../../../slices/auth-slice';
import { SidebarNavContent } from './SidebarNavContent';
import { DashboardNavDropdown } from './DashboardNavDropdown';

const APPLICATION_SERVER_KEY = process.env.REACT_APP_APPLICATION_SERVER_KEY;

export const TopNav = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const wide = useWindowWide(800);
  const [sidebar, setSidebar] = useState(false);
  const ref = useRef(null);
  const ref2 = useRef(null);
  const history = useHistory();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [dashboardNavMenuIsOpen, setDashboardNavMenuIsOpen] = useState(false);
  const [centerMenu, setCenterMenu] = useState(false);
  const dispatch = useDispatch();

  const location = useLocation();
  const pathname = location.pathname;

  const [notificationsModal, setNotificationsModal] = useState(false);
  const { t } = useTranslation('layout');

  const accessToken = userInfo?.token?.accessToken;

  useOnClickOutside(ref, () => menuIsOpen && setMenuIsOpen(false));
  useOnClickOutside(ref2, () => centerMenu && setCenterMenu(false));

  useEffect(() => {
    if (userInfo.user) {
      dispatch(getCurrentLicense());
    }
  }, []);

  // Keyed on the token so push setup retries once auth is available, instead of
  // silently giving up on a mount where the token was not hydrated yet.
  useEffect(() => {
    if (!accessToken) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    let cancelled = false;

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'Access-Control-Allow-Origin': '*'
      }
    };

    const subscribe = async () => {
      try {
        await navigator.serviceWorker.register('/sw.js');
        const reg = await navigator.serviceWorker.ready;
        if (cancelled) return;

        if (Notification.permission === 'denied') {
          await axios.delete(`/user/notification/web/device`, config);
          return;
        }

        if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted' || cancelled) return;
        }

        // Reuse the existing subscription rather than minting a new endpoint on
        // every mount.
        const existing = await reg.pushManager.getSubscription();
        const sub =
          existing ||
          (await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: APPLICATION_SERVER_KEY
          }));
        if (cancelled) return;

        const data = JSON.parse(JSON.stringify(sub));

        await axios.post(
          `/user/notification/web/device`,
          {
            endpoint: data.endpoint,
            p256dh: data.keys.p256dh,
            auth: data.keys.auth
          },
          config
        );
      } catch (error) {
        // Previously swallowed: a failed registration left push dead with no
        // trace anywhere.
        console.error('Push notification setup failed', error);
      }
    };

    subscribe();

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const showHide = ShowHide();
  const sidebarNavLinks = SidebarNavLinks();

  const isPartner = isPartnerHelper(
    userInfo?.user?.currentVirtualOfficeLicence?.dateEnd || ''
  );

  const refMenu = useRef(null);

  return (
    <AnimatePresence>
      {showHide && (
        <TopNavigation
          key='TopNavigation'
          initial={{ transform: 'translateY(-70px)' }}
          animate={{ transform: 'translateY(0px)' }}
          exit={{ transform: 'translateY(-70px)' }}
          transition={{
            duration: 0.2
          }}>
          <TopNavigationMain>
            <Flexed center gap='20px' position='relative'>
              {isPartner && (
                <div ref={refMenu}>
                  <DashboardNavDropdown
                    menuIsOpen={dashboardNavMenuIsOpen}
                    userInfo={userInfo}
                    setMenuIsOpen={setDashboardNavMenuIsOpen}
                    refMenu={refMenu}
                  />
                  <Icon
                    pointer
                    onClick={() =>
                      setDashboardNavMenuIsOpen(!dashboardNavMenuIsOpen)
                    }>
                    <HamburgerMenuIcon />
                  </Icon>
                </div>
              )}

              <img
                src={globalImages.logoOnlyLetter}
                alt='logo'
                style={{
                  height: 40,
                  cursor: 'pointer'
                }}
                onClick={() =>
                  history.push(
                    userInfo.userType === UserTypes.Regular
                      ? DASHBOARD
                      : MY_HUBS
                  )
                }
              />
            </Flexed>

            <MainNav>
              <Flexed center gap='10px' gapSM='5px' m='0 20px' mSM='0 10px'>
                <a
                  href='https://t.me/UnitedOne_Global'
                  target='_blank'
                  rel='noreferrer'>
                  <Icon pointer w='34px' color={colors.primary900}>
                    <IconTelegramNoBorder />
                  </Icon>
                </a>

                <div style={{ position: 'relative' }}>
                  <Banner
                    unseen={userInfo.unseenNotificationCount}
                    notification={true}
                  />

                  <Icon
                    onClick={() => {
                      setNotificationsModal(!notificationsModal);
                      dispatch(getNotifications());
                    }}
                    pointer
                    color={colors.primary900}>
                    <IconNotifications />
                  </Icon>
                </div>

                <Link to={!wide ? SETTINGS_LAYOUT : SETTINGS_ACCOUNT}>
                  <Icon pointer color={colors.primary900} w='25px'>
                    <IconSettings />
                  </Icon>
                </Link>
              </Flexed>

              <FlexCenter ref={ref}>
                <div
                  onClick={() => {
                    setMenuIsOpen(!menuIsOpen);
                  }}>
                  <UserImageCircle
                    image={userInfo?.user?.profileImageUrl}
                    name={userInfo?.user?.name}
                    surname={userInfo?.user?.surname}
                    size='40px'
                    fontSize='16px'
                    margin={wide ? '0 16px 0 0' : '0'}
                  />
                </div>

                <div
                  style={{
                    fontSize: '16px',
                    lineHeight: '34px',
                    position: 'relative'
                  }}>
                  <div
                    style={{
                      display: wide ? 'flex' : 'none',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setMenuIsOpen(!menuIsOpen);
                    }}>
                    <P2 bold color={colors.neutral900} mr='10'>
                      {userInfo?.user?.name}
                    </P2>

                    <ArrowPolygon direction={menuIsOpen ? 'up' : 'down'} />
                  </div>
                  <div>
                    <ProfileDropdown
                      menuIsOpen={menuIsOpen}
                      userInfo={userInfo}
                      setMenuIsOpen={setMenuIsOpen}
                    />
                  </div>
                </div>
              </FlexCenter>
            </MainNav>
          </TopNavigationMain>
          <SidebarNav open={sidebar} onClose={setSidebar} />
        </TopNavigation>
      )}
      <SidebarModal
        isOpen={notificationsModal}
        setOpen={setNotificationsModal}
        title={t('notifications.title')}
        sidebarWidth={'508px'}>
        <Notifications setNotificationsModal={setNotificationsModal} />
      </SidebarModal>
    </AnimatePresence>
  );
};
